import { useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';

// Hook for managing notifications — assumes polling is started by the layout
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(notificationService.isSocketConnected());

  useEffect(() => {
    const unsubscribeConnection = notificationService.subscribe('connection', (data) => {
      setIsConnected(data.connected);
    });

    const unsubscribeRead = notificationService.subscribe('notification-read', () => {
      setUnreadCount(notificationService.getUnreadCount());
    });

    const unsubscribeAllRead = notificationService.subscribe('all-notifications-read', () => {
      setUnreadCount(0);
    });

    // Each poll replaces the full feed (+ any newly-arrived items). The service
    // also toasts new items, so the hook just mirrors the latest state.
    const unsubscribeLoaded = notificationService.subscribe('notifications-loaded', ({ notifications: loaded }) => {
      setNotifications(loaded);
      setUnreadCount(loaded.filter((n) => !n.isRead).length);
    });

    const unsubscribeCleared = notificationService.subscribe('notifications-cleared', () => {
      setNotifications([]);
      setUnreadCount(0);
    });

    setNotifications(notificationService.getNotifications());
    setUnreadCount(notificationService.getUnreadCount());

    return () => {
      unsubscribeConnection();
      unsubscribeRead();
      unsubscribeAllRead();
      unsubscribeLoaded();
      unsubscribeCleared();
    };
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    notificationService.markAsRead(notificationId);
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    notificationService.markAllAsRead();
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    notificationService.clearNotifications();
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Refresh notifications — re-poll the persisted feed from the server.
  // 'notifications-loaded' fires on completion and updates state.
  const refreshNotifications = useCallback(() => {
    notificationService.loadPersisted(false);
  }, []);

  return {
    notifications,
    unreadCount,
    isConnected,
    isLoading: false, // Add loading state if needed
    markAsRead,
    markAllAsRead,
    clearNotifications,
    refreshNotifications
  };
};